/**
 * Survey contract types — docs/superpowers/specs/2026-10-08-surveys-part1.md.
 * The server has the same shapes (server/src/modules/survey/survey.types.ts).
 */

export type Lang = "en" | "hi";
export const LANGS: readonly Lang[] = ["en", "hi"] as const;

export type Localized = { en: string; hi: string };

export type SurveyStatus = "draft" | "active" | "paused" | "ended";

export interface SurveyContent {
  intro: { title: Localized; body: Localized };
  thankYou: { title: Localized; body: Localized };
  feedback: { enabled: boolean; prompt: Localized; maxChars: number };
}

export type QuestionType =
  | "single_choice"
  | "multi_choice"
  | "rating"
  | "nps"
  | "text";

export const QUESTION_TYPES: readonly QuestionType[] = [
  "single_choice",
  "multi_choice",
  "rating",
  "nps",
  "text",
] as const;

export interface SurveyOption {
  key: string;
  text: Localized;
}

export interface SurveyQuestion {
  id: string;
  type: QuestionType;
  text: Localized;
  required: boolean;
  options?: SurveyOption[];
  allowOther?: boolean;
  maxSelect?: number;
  rating?: { scale: number; style: "faces" | "stars" };
  nps?: { lowLabel: Localized; highLabel: Localized };
}

export interface SurveyAudience {
  activeWithinDays?: number;
  inactiveForDays?: number;
  minExpenses?: number;
  minAccountAgeDays?: number;
  maxAccountAgeDays?: number;
  group?: "any" | "shared" | "personal_only";
  platforms?: ("ios" | "android")[];
  minAppVersion?: string;
  languages?: Lang[];
  samplePercent?: number;
}

/** What the audience filters look at, per user. */
export interface UserFacts {
  createdAt: Date;
  lastActiveAt: Date | null;
  locale: string;
  platform: string | null;
  appVersion: string | null;
  expenseCount: number;
  inSharedGroup: boolean;
}

/** Editable settings besides content / questions / audience. */
export interface SurveySettings {
  title: string;
  startAt: string | null; // ISO
  endAt: string | null; // ISO
  responseLimit: number | null;
  maxShows: number;
  showCooldownHours: number;
  maxDismissals: number;
  testEmails: string[];
}

/** Full editable survey as the editor sends it. */
export interface SurveyInput extends SurveySettings {
  content: SurveyContent;
  questions: SurveyQuestion[];
  audience: SurveyAudience;
}

export interface SurveyRecord extends SurveyInput {
  id: string;
  status: SurveyStatus;
  placement: string;
  cohortFrozenAt: string | null;
  publishedAt: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

/** Stored answer per question. */
export interface StoredAnswer {
  value: unknown;
  other?: string;
}

export interface ResponseRow {
  userId: string;
  email: string;
  status: "shown" | "started" | "completed" | "dismissed";
  language: string | null;
  answers: Record<string, StoredAnswer>;
  feedback: string | null;
  shownCount: number;
  dismissedCount: number;
  startedAt: Date | null;
  completedAt: Date | null;
  appVersion: string | null;
  platform: string | null;
}
